import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import SupplierRatingDetailsTab from './components/tabs/SupplierRatingDetailsTab';
import SupplierRatingAuditLogTab from './components/tabs/SupplierRatingAuditLogTab';
import { useFlexibleList } from '../../hooks/useSWR';
import { DocumentTextIcon, ClipboardCheckIcon, XIcon } from '../../components/ui/Icons';

/**
 * Custom Split View Panel for Supplier Rating module with responsive tab carousel
 * 
 * Features:
 * - Smart Detection: Automatically calculates how many tabs can fit based on drawer width
 * - Config-Based: Uses actual drawerWidth from module config for precise calculations
 * - Clean Navigation: Only shows Previous/Next arrows when needed
 * - No Overflow: All elements stay within panel boundaries
 * - Responsive: Adapts to any drawer width configuration
 * 
 * Tabs:
 * - Details: Supplier rating information and details
 * - Audit Log: Complete audit trail for the supplier rating
 */
const CustomSupplierRatingSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels,
  onClose,
  onRecordUpdate // Callback to refresh Supplier Rating data
}) => {
  const { theme } = useTheme();
  // Fetch Supplier Rating audit logs for badge
  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/entity/9/${selectedRecord.id}` : null
  );

  // Define all tabs with their properties
  const allTabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: 0,
      badgeColor: "bg-green-600"
    },
    {
      value: 'audit-log',
      label: 'Audit Log',
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: "bg-red-600"
    }
  ];

  if (!isOpen) return null;

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div className={cn("min-h-0 h-full rounded-xl border flex flex-col", theme.border.light, theme.colors.surface, theme.shadow.md)}>
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn("text-sm font-semibold", theme.text.primary)}>
            {selectedRecord ? `Supplier Rating - ${selectedRecord.supplier?.name || selectedRecord.id}` : "Select a supplier rating to view details"}
          </div>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:opacity-80", theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Content */}
        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />
            
            <div className="flex-1 flex flex-col min-h-0">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <SupplierRatingDetailsTab
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={selectedRecordFiles}
                  config={config}
                  fields={fields}
                />
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <SupplierRatingAuditLogTab
                  selectedRecord={selectedRecord}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn("p-4 text-sm", theme.text.secondary)}>
            {labels.noSelectionText || "No supplier rating selected"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSupplierRatingSplitView;
